// src/components/TaskChat/ChatMessageList.tsx
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import TaskAction from "../../../../interfaces/task/taskActions/TaskAction";
import UserInfo from "../../../../interfaces/UserInfo";
import Avatar from "../../../../interfaces/avatar/Avatar";
import AvatarImage from "../../../ui/Avatar/AvatarImage";
import { deleteTaskAction, updateTaskAction } from "../../../../services/taskServices/TaskActionService";
import { TaskActionUpdateRequest } from "../../../../interfaces/task/taskActions/TaskActionUpdateRequest";
import { toast } from "react-toastify";
import "./ChatMessageList.css";
import { useAuth } from "../../../../firebase/AuthProvider";
import NoDataFound from "../NoDataFound";

interface ChatMessageListProps {
  chats: TaskAction[];
  users: UserInfo[] | null;
  avatars: Avatar[] | null;
  currentUserId?: string;
  onDelete?: (actionId: number) => void; // Callback to refresh parent component
  onUpdate?: (actionId: number, newText: string) => void; // Callback to update comment
}

const ChatMessageList: React.FC<ChatMessageListProps> = ({
  chats,
  users,
  avatars,
  currentUserId,
  onDelete,
  onUpdate,
}) => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editText, setEditText] = useState<string>("");
  const [localChats, setLocalChats] = useState<TaskAction[]>(chats);
  const [currentTime, setCurrentTime] = useState<Date>(new Date()); // For live timestamp updates

  // Update current time for live timestamp updates
  // More frequent updates for better UX (every 10 seconds)
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 10000); // Update every 10 seconds for live updates

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop =
        scrollContainerRef.current.scrollHeight;
    }
  }, [chats, localChats]);

  useEffect(() => {
    setLocalChats(chats);
  }, [chats]);

  const handleDelete = async (actionId: number, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent any parent click handlers
    
    console.log(" Delete Debug - actionId:", actionId);
    console.log(" Delete Debug - typeof actionId:", typeof actionId);
    console.log(" Delete Debug - actionId is NaN:", isNaN(actionId));
    
    if (!window.confirm("Are you sure you want to delete this message?")) {
      return;
    }

    if (!currentUser) {
      toast.error("User not authenticated");
      return;
    }
    
    try {
      setDeletingId(actionId);
      
      // Get auth token from current user
      const authToken: string = await currentUser.getIdToken();
      
      console.log(" Delete Debug - Calling API with actionId:", actionId);
      const success = await deleteTaskAction(authToken, actionId);

      
      
      if (success) {
        toast.success("Message deleted successfully");
        // Remove from local state immediately
        setLocalChats(prev => prev.filter(chat => chat.id !== actionId));
        // Call parent callback to refresh parent component
        if (onDelete) {
          onDelete(actionId);
        }
      } else {
        toast.error("Failed to delete message");
      }
    } catch (error) {
      console.error("Error deleting message:", error);
      toast.error("Error deleting message");
    } finally {
      setDeletingId(null);
    }
  };

  const handleEdit = (actionId: number, currentText: string) => {
    setEditingId(actionId);
    setEditText(currentText);
  };

  const handleSaveEdit = async (actionId: number) => {
    if (!currentUser || !editText.trim()) {
      setEditingId(null);
      setEditText("");
      return;
    }

    try {
      const authToken = await currentUser.getIdToken();
      console.log("🔍 Save Edit - actionId:", actionId);
      console.log("🔍 Save Edit - newText:", editText);
      
      // Create TaskActionUpdateRequest array for the backend API
      const updateRequest: TaskActionUpdateRequest = {
        taskActionId: actionId.toString(),
        description: editText,
        completionTime: new Date(),
        deleted: false
      };

      // Call the existing updateTaskAction API
      const response = await updateTaskAction(authToken, [updateRequest]);
      
      if (response.success) {
        // Update local state immediately with both description and completionTime
        const updatedCompletionTime = new Date();
        setLocalChats(prev => prev.map(chat => 
          chat.id === actionId ? { ...chat, description: editText, completionTime: updatedCompletionTime } : chat
        ));
        if (onUpdate) {
          onUpdate(actionId, editText);
        }
        setEditingId(null);
        setEditText("");
        toast.success("Comment updated successfully");
      } else {
        toast.error("Failed to update comment");
      }
    } catch (error) {
      console.error("Error updating comment:", error);
      toast.error("Error updating comment");
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditText("");
  };

  const renderDescription = (description: string) => {
    if (!description) return null;
    let parts = description.split(/({user:[^}]+}|\n)/g);
    return parts.map((part, i) => {
      if (part === "\n") return <br key={i} />;
      const match = part.match(/{user:(.+)}/);
      if (match) {
        const userId = match[1];
        const mentionedUser = users?.find((user) => user.id === userId);
        return mentionedUser ? (
          <span
            key={i}
            className="chat-message__mention"
            onClick={() => navigate(`/profile/user/${mentionedUser.id}`)}
          >
            {mentionedUser.displayName}
          </span>
        ) : (
          `{Unknown User}`
        );
      }
      return part;
    });
  };

  const findAvatarById = (userId: string | undefined): string | null => {
    if (userId) {
      const avatarObj = avatars?.find((avatar) => avatar.id === userId);
      return avatarObj ? `data:image/png;base64,${avatarObj.avatar}` : null;
    } else {
      return null;
    }
  };

  const formatTimestamp = (date: Date | string | undefined) => {
    if (!date) return "";
    const now = currentTime; // Use currentTime state for live updates (already in local timezone)
    
    // Parse the date - handle UTC strings properly
    // Dates from API come as UTC strings (e.g., "2025-12-18T05:19:33.191813" without 'Z')
    let messageDate: Date;
    if (typeof date === 'string') {
      // Check if the string has 'T' but no 'Z' or timezone offset - this is the UTC format from API
      if (date.includes('T') && !date.includes('Z') && !date.includes('GMT') && !date.match(/[+-]\d{2}:\d{2}$/)) {
        // This is UTC time without timezone indicator - append 'Z' to treat as UTC
        messageDate = new Date(date + 'Z');
      } else {
        // Has timezone info or is in a different format, parse directly
        messageDate = new Date(date);
      }
    } else {
      messageDate = date;
    }
    
    // Validate the date
    if (isNaN(messageDate.getTime())) {
      return "";
    }
    
    // Both dates are now in local timezone (JavaScript Date automatically converts UTC to local)
    // Calculate the difference
    const diffInMs = now.getTime() - messageDate.getTime();
    
    // Handle negative differences (future dates)
    if (diffInMs < 0) {
      return "just now";
    }

    const diffInSeconds = Math.floor(diffInMs / 1000);
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    // Just now (less than 1 minute)
    if (diffInSeconds < 60) {
      return "just now";
    }
    // Minutes ago (less than 1 hour)
    else if (diffInMinutes < 60) {
      return diffInMinutes === 1 ? "1 minute ago" : `${diffInMinutes} minutes ago`;
    }
    // Hours ago (less than 24 hours)
    else if (diffInHours < 24) {
      return diffInHours === 1 ? "1 hour ago" : `${diffInHours} hours ago`;
    }
    // Days ago (less than 7 days)
    else if (diffInDays < 7) {
      return diffInDays === 1 ? "1 day ago" : `${diffInDays} days ago`;
    }
    // Older than 7 days - show date in local timezone
    else {
      return messageDate.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }
  };

  return (
    <div className="chat-message-list" ref={scrollContainerRef}>
      {localChats.length === 0 ? (
        <NoDataFound 
          title="No Comments Found"
          description="There are no comments to show you right now"
        />
      ) : (
        localChats.map((chat, index) => {
          const avatarUrl = findAvatarById(chat.author?.id);
          const initials =
            chat.author?.firstName && chat.author.lastName
              ? chat.author.firstName[0] + chat.author.lastName[0]
              : "??";
          const isCurrentUser = chat.author?.id === currentUserId;
          const authorName = chat.author
            ? ((chat.author.firstName == null && chat.author.lastName == null)
                ? "Unknown User"
                : `${chat.author.firstName || ""} ${chat.author.lastName || ""}`.trim())
            : "Unknown User";

          return (
          <> {
            chat?.actionType?.actionTypeName !="SYSTEM MESSAGE" && (
              <div
              key={index}
              className={`chat-message-item ${
                chat.isNew ? "chat-message-item--new" : ""
              }`}
            >
              <div className="chat-message-item__avatar">
                <AvatarImage userId={chat.author ? chat.author.id : "-1"} />
              </div>
              <div className="chat-message-item__content">
                <div className="chat-message-item__header">
                  <div className="flex items-center gap-[1.3rem]">
                    <span className="chat-message-item__author">
                      {authorName}
                    </span>
                    <span className="chat-message-item__timestamp">
                      {formatTimestamp(chat.completionTime)}
                    </span>
                  </div>
                  {/* <div className="chat-message-item__actions">
                    {editingId === chat.id ? (
                      // Edit Mode UI
                      <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                        <input
                          type="text"
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          style={{
                            flex: 1,
                            padding: "0.25rem 0.5rem",
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            fontSize: "14px"
                          }}
                          autoFocus
                        />
                        <button
                          onClick={() => handleSaveEdit(chat.id)}
                          style={{
                            padding: "0.25rem 0.5rem",
                            backgroundColor: "#007bff",
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                            cursor: "pointer"
                          }}
                        >
                          Save
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          style={{
                            padding: "0.25rem 0.5rem",
                            backgroundColor: "#6c757d",
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                            cursor: "pointer"
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      // Normal Mode UI
                      <button 
                        className="chat-message-item__action-btn"
                        onClick={(e) => {
                          const id = typeof chat.id === 'string' ? parseInt(chat.id) : chat.id;
                          console.log("🔍 Button Click Debug - chat.id:", chat.id);
                          console.log("🔍 Button Click Debug - parsed id:", id);
                          handleEdit(id, chat.description || "");
                        }}
                        disabled={deletingId === chat.id}
                        style={{ 
                          opacity: deletingId === chat.id ? 0.6 : 1,
                          cursor: deletingId === chat.id ? 'not-allowed' : 'pointer'
                        }}
                      >
                        Edit
                      </button>
                    )}
                    <span className="chat-message-item__separator">.</span>
                    <button 
                      className="chat-message-item__action-btn"
                      onClick={(e) => {
                        const id = typeof chat.id === 'string' ? parseInt(chat.id) : chat.id;
                        console.log("🔍 Button Click Debug - chat.id:", chat.id);
                        console.log("🔍 Button Click Debug - parsed id:", id);
                        handleDelete(id, e);
                      }}
                      disabled={deletingId === chat.id}
                      style={{ 
                        opacity: deletingId === chat.id ? 0.6 : 1,
                        cursor: deletingId === chat.id ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {deletingId === chat.id ? 'Deleting...' : 'Delete'}
                    </button>
                  </div> */}
                </div>
                <div className="chat-message-item__message">
                  <p>{renderDescription(chat.description ?? "")}</p>
                </div> 
                {/* my comment  */}
              </div>
            </div>
            )

          }
           
            </>
          );
        })
      )}
    </div>
  );
};

export default ChatMessageList;
